import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PageMeta } from "@/components/PageMeta";
import { ROUTES } from "@/config/links";
import { useLang, useT } from "@/i18n";
import {
  capture, captureOaiqEvent, captureOaiqLeadCreated,
  trackAssetViewed, trackDiagnosticStarted, trackDiagnosticQuestionsCompleted,
  trackResultsViewed,
  trackFormStarted, trackFormSubmitAttempted, trackFormSubmissionFailed, generateFormAttemptId,
} from "@/lib/analytics";
import * as api from "./api";
import { DiagnosticLayout } from "@/components/diagnostics/DiagnosticLayout";
import { LeftRail } from "@/components/diagnostics/LeftRail";
import { IntroPanel } from "@/components/diagnostics/IntroPanel";
import { QuestionPanel } from "@/components/diagnostics/QuestionPanel";
import { ContactPanel } from "@/components/diagnostics/ContactPanel";
import { ResultsCard } from "./ResultsCard";

const DIAGNOSTIC_ID = "next_presentation_readiness";
// InterAct Tracking Foundation milestone (2026-10-09) — stable form_id
// for this diagnostic's own contact-capture step (§3 "form attempt/
// failure"). Not a registered standalone `form_id` in asset_registry.json
// (that field is reserved for independent forms like Contact/Playbook);
// this one is scoped under the diagnostic's own asset_id instead.
const CONTACT_FORM_ID = "next_presentation_readiness_contact";

/**
 * Orchestrates one diagnostic run end-to-end: intro -> one question at a
 * time (context -> gate -> scored -> post-diagnostic, §5.3) -> contact
 * form -> Results Page (§8). All branching/scoring/copy is config-driven
 * from the `/definition` + `/complete` API responses — nothing about a
 * specific diagnostic is hardcoded here, so a second diagnostic reuses
 * this exact page by swapping `DIAGNOSTIC_ID`. The presentation layer
 * (2026-02 full-screen redesign — see `components/diagnostics/`) is the
 * same shared shell/panels the Executive Presence Communication
 * Diagnostic uses, for visual consistency across both diagnostics.
 * 2026-02 — also PT/EL localized the same way EP is: `lang` drives
 * which locale's definition loads and which locale a run is locked to.
 */
export default function NextPresentationReadinessCheck() {
  const navigate = useNavigate();
  const t = useT();
  const { lang, l } = useLang(); // "en" / "pt" / "el" — drives which locale's diagnostic content loads
  const [definition, setDefinition] = useState(null);
  const [stage, setStage] = useState("loading"); // loading | intro | running | contact | results | error
  const [starting, setStarting] = useState(false);
  const [runId, setRunId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [freeTexts, setFreeTexts] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [submittingContact, setSubmittingContact] = useState(false);
  const [contactError, setContactError] = useState(null);
  const [result, setResult] = useState(null);
  const [email, setEmail] = useState("");
  const abandonedFiredRef = useRef(false);
  const startedAtRef = useRef(null);
  const assetViewedFiredRef = useRef(false);
  const questionsCompletedFiredRef = useRef(false);
  const resultsViewedFiredRef = useRef(false);
  const contactFormAttemptIdRef = useRef(null);
  // InterAct Tracking Foundation milestone (2026-10-10) — the CTA that
  // led here (if any), captured one-shot at the SAME `trackAssetViewed`
  // call the asset_viewed event below already fires.
  const ctaNavContextRef = useRef(null);

  const misc = definition?.miscCopy || {};

  useEffect(() => {
    api
      .getDefinition(DIAGNOSTIC_ID, lang)
      .then((def) => {
        setDefinition(def);
        setStage("intro");
      })
      .catch(() => setStage("error"));
  }, [lang]);

  /**
   * P0 `asset_viewed` — once per page view, the moment the intro
   * actually renders (not merely "the route matched"). `cta_source`
   * (if any) is picked up automatically from whichever CTA linked
   * here, via `trackAssetViewed`'s own sessionStorage read.
   */
  useEffect(() => {
    if (stage !== "intro" || assetViewedFiredRef.current) return;
    assetViewedFiredRef.current = true;
    ctaNavContextRef.current = trackAssetViewed(DIAGNOSTIC_ID, { placement: "full_page" });
  }, [stage]);

  const effectiveQuestions = useMemo(() => {
    if (!definition) return [];
    const gateAnswer = answers.gate_slides;
    return definition.questions.filter(
      (q) => !(gateAnswer === "no" && q.categoryId === "slides_visual_support"),
    );
  }, [definition, answers.gate_slides]);

  /**
   * "Diagnostic abandoned" — best-effort only. A started run (stage
   * running/contact, i.e. past the intro, not yet at results) fires
   * once per run via the `pagehide` event (the page is actually being
   * unloaded — navigated away from, closed, or put into bfcache), NOT
   * `visibilitychange`. No field values or free-text answers are
   * included, only the run's structural position.
   *
   * 2026-10-21 fix: this used to listen for `visibilitychange` going
   * to "hidden", which fires on ANY tab/app backgrounding — switching
   * apps, locking the phone, a notification overlay — not just a
   * genuine exit. A real visitor briefly backgrounded the tab right
   * after starting, got marked "abandoned" at that instant, then
   * returned 11+ minutes later and kept answering normally: a
   * completed-in-spirit session permanently mislabeled as abandoned.
   * `pagehide` only fires when the page is actually going away, so a
   * visitor who backgrounds and comes back no longer fires this at
   * all — exactly the false positive this diagnostic produced.
   */
  const buildAbandonedProps = (reason) => {
    const questionTotal = effectiveQuestions.length;
    const progressPercent =
      stage === "contact" ? 100 : questionTotal ? Math.round((currentIndex / questionTotal) * 100) : 0;
    return {
      diagnostic_id: DIAGNOSTIC_ID,
      run_id: runId,
      stage_at_exit: stage,
      current_question_id: stage === "running" ? effectiveQuestions[currentIndex]?.questionId || null : null,
      question_index: stage === "running" ? currentIndex : null,
      question_total: stage === "running" ? questionTotal : null,
      progress_percent: progressPercent,
      page_path: window.location.pathname,
      time_spent_ms: startedAtRef.current ? Date.now() - startedAtRef.current : null,
      reason,
    };
  };

  useEffect(() => {
    if (stage !== "running" && stage !== "contact") return;
    const onPageHide = () => {
      if (abandonedFiredRef.current) return;
      abandonedFiredRef.current = true;
      capture("diagnostic_abandoned", buildAbandonedProps("page_hidden"));
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [stage, runId, currentIndex, effectiveQuestions.length]);

  const handleStart = async () => {
    setStarting(true);
    abandonedFiredRef.current = false;
    try {
      const { runId: newRunId } = await api.startRun(DIAGNOSTIC_ID, { ...(await api.currentUrlAttribution()), locale: lang });
      startedAtRef.current = Date.now();
      setRunId(newRunId);
      setCurrentIndex(0);
      setStage("running");
      trackDiagnosticStarted({ assetId: DIAGNOSTIC_ID, runId: newRunId });
      captureOaiqEvent("diagnostic_started");
      // ChatGPT Ads custom conversion events, pass 2 (2026-02) — the
      // ACTUAL start (server-confirmed run_id just returned by
      // `api.startRun()`), never a page view. Fires alongside the
      // existing generic `diagnostic_started` call above, not
      // replacing it.
      captureOaiqEvent("presentation_check_started", `presentation_check_started:${newRunId}`);
    } catch {
      setStage("error");
    } finally {
      setStarting(false);
    }
  };

  /**
   * 2026-10-21 incident fix: this used to be `handleSelect`, called
   * IMMEDIATELY on every option click, which scheduled its own
   * `setTimeout`-based advance — two clicks on the same question
   * (changing an answer) scheduled two advances, silently skipping
   * the next question. Now there is exactly one call site: the
   * QuestionPanel's own explicit "Next" button, which calls this
   * once per click (guarded on its own side) with the FINAL chosen
   * answer for the CURRENT question — the advance below is therefore
   * a direct, synchronous, one-shot state transition with no timer
   * and no way to be triggered twice for the same question.
   */
  const handleNext = (optionId, freeText) => {
    const questionId = currentQuestion.questionId;
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
    setFreeTexts((prev) => {
      const next = { ...prev };
      if (freeText !== null && freeText !== undefined && freeText !== "") next[questionId] = freeText;
      else delete next[questionId];
      return next;
    });
    // Not retried/surfaced to the visitor here — the pre-submit
    // completeness check and the backend-400 recovery path (both in
    // `handleContactSubmit`) already catch a question that never
    // actually persisted, by the time of final submission. Logged so
    // a real persistence failure is at least visible, not fully silent.
    api.putAnswer(runId, questionId, optionId, freeText).catch((err) => console.error("putAnswer failed", questionId, err));

    const newAnswers = { ...answers, [questionId]: optionId };
    const gateAnswer = newAnswers.gate_slides;
    const newEffectiveQuestions = definition.questions.filter(
      (q) => !(gateAnswer === "no" && q.categoryId === "slides_visual_support"),
    );
    // Plain computation from the current render's `currentIndex` — no
    // functional updater needed (there is no longer any risk of two
    // overlapping advances for one question, see QuestionPanel.jsx),
    // so a side effect (setStage) no longer needs to live inside a
    // state updater callback.
    const nextIndex = currentIndex + 1;
    if (nextIndex >= newEffectiveQuestions.length) {
      setStage("contact");
    } else {
      setCurrentIndex(nextIndex);
    }
  };

  const handleBack = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  /**
   * P0 `diagnostic_questions_completed` — fires exactly once, the
   * moment every scored question has been answered and the contact-
   * capture step (ContactPanel) is about to render. `form_started` is
   * NOT fired here (2026-10-09 correction) — it must reflect the
   * visitor's own first field mutation inside ContactPanel, not merely
   * that this stage was reached; see `handleFirstContactFieldMutation`.
   */
  useEffect(() => {
    if (stage !== "contact" || questionsCompletedFiredRef.current) return;
    questionsCompletedFiredRef.current = true;
    trackDiagnosticQuestionsCompleted({ assetId: DIAGNOSTIC_ID, runId });
  }, [stage, runId]);

  /** P0 `results_viewed` — fires once the results screen actually renders. */
  useEffect(() => {
    if (stage !== "results" || resultsViewedFiredRef.current) return;
    resultsViewedFiredRef.current = true;
    trackResultsViewed({ assetId: DIAGNOSTIC_ID, runId });
  }, [stage, runId]);

  const handleExit = () => {
    if (!abandonedFiredRef.current && (stage === "running" || stage === "contact")) {
      abandonedFiredRef.current = true;
      capture("diagnostic_abandoned", buildAbandonedProps("exit_button"));
    }
    navigate(l("/"));
  };

  /**
   * P0 `form_started` — tracking_registry.json: "First field mutation
   * without value capture". One `form_attempt_id`, generated here at
   * the ACTUAL first mutation (never earlier), reused as-is for the
   * `form_submit_attempted`/`form_submission_failed` of this same
   * attempt below — not regenerated per submit click.
   */
  const handleFirstContactFieldMutation = () => {
    if (contactFormAttemptIdRef.current) return;
    contactFormAttemptIdRef.current = generateFormAttemptId();
    trackFormStarted({ formId: CONTACT_FORM_ID, assetId: DIAGNOSTIC_ID, formAttemptId: contactFormAttemptIdRef.current });
  };

  const handleContactSubmit = async ({ email: submittedEmail, firstName, role, otherRoleText, company, marketingConsent }) => {
    // Defense-in-depth (2026-10-21 fix): verify every question the
    // visitor was actually shown has a stored answer BEFORE calling
    // the backend at all. With the Next-button rewrite this should
    // always already hold, but if it ever doesn't, send the visitor
    // straight back to the missing question with their progress
    // intact instead of letting a doomed request reach the API.
    const missingQuestion = effectiveQuestions.find((q) => !answers[q.questionId]);
    if (missingQuestion) {
      const missingIdx = effectiveQuestions.indexOf(missingQuestion);
      toast.error(t("diagnosticUi.missing_answer_recovery"));
      setCurrentIndex(missingIdx);
      setStage("running");
      return;
    }
    setSubmittingContact(true);
    setContactError(null);
    // Defensive fallback only (e.g. browser autofill submitted with no
    // visible onChange firing first) — form_started would have been
    // missed in that edge case, but submit/failure must still carry a
    // real, usable form_attempt_id rather than `null`.
    if (!contactFormAttemptIdRef.current) contactFormAttemptIdRef.current = generateFormAttemptId();
    const formAttemptId = contactFormAttemptIdRef.current;
    // A FRESH attempt_id per submit CLICK (distinct from the one
    // long-lived form_attempt_id above) — a resubmission after a
    // failure is its own attempt_id, still correlated to the same
    // form_attempt_id session.
    const attemptId = generateFormAttemptId();
    trackFormSubmitAttempted({ formId: CONTACT_FORM_ID, assetId: DIAGNOSTIC_ID, formAttemptId, attemptId });
    try {
      const data = await api.completeRun(runId, {
        email: submittedEmail, firstName, role, otherRoleText, company, marketingConsent,
        ctaNavContext: ctaNavContextRef.current, runId,
      });
      setResult(data.result);
      setEmail(submittedEmail);
      setStage("results");
      // P0 `diagnostic_completed` is NOT fired from the browser here
      // (2026-10-09 correction) — the backend's `complete_run()` (just
      // awaited above) already emitted the one authoritative
      // `diagnostic_completed` outbox event immediately after
      // `runs.complete_run` persisted this result. A second, frontend
      // publisher of the same canonical event is exactly what
      // `trackDiagnosticCompleted`'s own doc comment now warns against.
      // `runId` is already this run's own backend idempotency key for
      // `/complete` — reused unchanged as the OpenAI Pixel's official
      // `event_id` dedup key (lead-created tracking audit, 2026-02).
      captureOaiqLeadCreated(runId);
      // ChatGPT Ads custom conversion events, pass 2 (2026-02) —
      // successful completion only (this `try` block's success path,
      // after `completeRun()` already resolved) — same `runId`.
      captureOaiqEvent("presentation_check_completed", `presentation_check_completed:${runId}`);
    } catch (err) {
      const errorCode = !err.status ? "network_error" : err.status >= 500 ? "server_error" : err.status === 429 ? "rate_limited" : "validation_error";
      trackFormSubmissionFailed({ formId: CONTACT_FORM_ID, assetId: DIAGNOSTIC_ID, formAttemptId, attemptId, errorCode });
      // Recovery path (2026-10-21 fix): the backend's own completeness
      // check (`score_run`) names the unanswered question by id in its
      // 400 detail — e.g. "missing/invalid answer for scored question
      // 'gate_slides' ...". If this is that specific, well-known shape,
      // send the visitor back to answer it instead of leaving them on
      // a dead-end error with no path forward (exactly what produced
      // the 6 identical failed retries on 2026-10-21).
      const missingMatch = /scored question '([^']+)'/.exec(err.message || "");
      const missingIdx = missingMatch ? effectiveQuestions.findIndex((q) => q.questionId === missingMatch[1]) : -1;
      if (missingIdx !== -1) {
        toast.error(t("diagnosticUi.missing_answer_recovery"));
        setCurrentIndex(missingIdx);
        setStage("running");
      } else {
        setContactError(err.message || t("diagnosticUi.generic_submit_error"));
      }
    } finally {
      setSubmittingContact(false);
    }
  };

  const currentQuestion = effectiveQuestions[currentIndex];

  return (
    <>
      <PageMeta
        titleKey="nextPresentationReadinessCheck.title"
        descriptionKey="nextPresentationReadinessCheck.description"
        path={ROUTES.nextPresentationReadinessCheck}
      />
      <DiagnosticLayout showExit={stage === "running"} onExit={handleExit} showLanguagePrompt={stage === "intro"}>
        {stage === "loading" && (
          <div className="min-h-[70vh] flex items-center justify-center text-sm text-ink-soft">Loading…</div>
        )}
        {stage === "error" && (
          <div className="min-h-[70vh] flex items-center justify-center text-sm text-terracotta-dark" data-testid="readiness-error">
            Something went wrong loading the readiness check. Please refresh and try again.
          </div>
        )}

        {(stage === "intro" || stage === "running" || stage === "contact") && (
          <div className="flex flex-col lg:flex-row" data-testid="readiness-diagnostic-root">
            <LeftRail
              eyebrow={definition?.clientFacingTitle || ""}
              stepIndex={stage === "intro" ? 0 : stage === "contact" ? effectiveQuestions.length : currentIndex + 1}
              stepTotal={effectiveQuestions.length}
              blurb={definition?.introCopy?.meta || ""}
            />
            <main className="flex-1 flex items-start justify-center px-5 md:px-12 py-10 md:py-16">
              <div className="w-full max-w-xl">
                {stage === "intro" && definition && (
                  <IntroPanel introCopy={definition.introCopy} onStart={handleStart} starting={starting} />
                )}
                {stage === "running" && currentQuestion && (
                  <QuestionPanel
                    key={currentQuestion.questionId}
                    question={currentQuestion}
                    selectedOptionId={answers[currentQuestion.questionId]}
                    freeTextValue={freeTexts[currentQuestion.questionId]}
                    canGoBack={currentIndex > 0}
                    onNext={handleNext}
                    onBack={handleBack}
                  />
                )}
                {stage === "contact" && (
                  <ContactPanel
                    onSubmit={handleContactSubmit}
                    submitting={submittingContact}
                    error={contactError}
                    headingText={misc.contact_heading}
                    subheadingText={misc.contact_subheading}
                    consentText={misc.contact_consent_text}
                    onFirstFieldMutation={handleFirstContactFieldMutation}
                  />
                )}
              </div>
            </main>
          </div>
        )}

        {stage === "results" && result && (
          <ResultsCard result={result} email={email} misc={misc} />
        )}
      </DiagnosticLayout>
    </>
  );
}
